import * as React from "react"
import { CategorySubtype, type EntityCategory } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgInfoCircle } from "@digits-shared/components/SVGIcons/line/InfoCircle.svg"
import { Select, SelectItem } from "@digits-shared/DesignSystem/FormElements/Selects/Select"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import colors from "@digits-shared/themes/colors"
import { useAtom, useAtomValue } from "jotai"
import type { PrimitiveAtom } from "jotai/index"
import styled from "styled-components"
import {
  InputGroup,
  PanelLabel,
} from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Shared"
import {
  CATEGORY_SUBTYPE_DISPLAY_MAP,
  CATEGORY_SUBTYPES_MAP,
} from "src/frontend/components/OS/Applications/CategoryManager/Shared"
import { SVGIconComponent } from "src/shared/components/Icons/SVGIcon"

const StyledInputGroup = styled(InputGroup)`
  position: relative;
`

const SubtypeIcon = styled(SVGIconComponent)`
  width: 24px;
  height: 24px;
  padding: 2px;
  ${svgPathStyles(colors.secondary, 1.5)};

  &.disabled {
    ${svgPathStyles(colors.secondary50, 1.5)};
  }
`

const IconPosition = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
`

const InfoIcon = styled(SvgInfoCircle)`
  width: 16px;
  height: 16px;
  ${svgPathStyles(colors.secondary80, 1.5)};
`

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 24px;
`

interface SubtypeSelectProps {
  subtypeAtom: PrimitiveAtom<CategorySubtype | undefined>
  parentCategoryAtom: PrimitiveAtom<EntityCategory | undefined>
  readOnly?: boolean
}

export const SubtypeSelect: React.FC<SubtypeSelectProps> = ({
  subtypeAtom,
  parentCategoryAtom,
  readOnly,
}) => {
  const parentCategory = useAtomValue(parentCategoryAtom)
  const [subtype, setSubtype] = useAtom(subtypeAtom)

  const disabled = React.useMemo(
    () => !parentCategory || parentCategory.subtype !== CategorySubtype.None,
    [parentCategory]
  )

  const subtypeOptions = React.useMemo(
    () => (parentCategory ? CATEGORY_SUBTYPES_MAP.get(parentCategory.type) || [] : []),
    [parentCategory]
  )

  const onValueChange = React.useCallback(
    (value: CategorySubtype) => {
      setSubtype(value)
    },
    [setSubtype]
  )

  return (
    <StyledInputGroup>
      <PanelLabel>Subtype</PanelLabel>

      <Select
        placeholder="Select subtype…"
        value={subtype || ""}
        onValueChange={onValueChange}
        disabled={disabled || readOnly}
      >
        {subtypeOptions.map((s) => {
          const { label, displayKey } = CATEGORY_SUBTYPE_DISPLAY_MAP.get(s) || {
            label: s,
            displayKey: "Tag01",
          }
          return (
            <SelectItem key={s} value={s}>
              <ItemContent>
                <SubtypeIcon
                  subjectDisplayKey={displayKey}
                  className={disabled ? "disabled" : ""}
                />
                {label}
              </ItemContent>
            </SelectItem>
          )
        })}
      </Select>

      {disabled && subtype && (
        <IconPosition>
          <DigitsTooltip content="Subtype set by parent category">
            <InfoIcon />
          </DigitsTooltip>
        </IconPosition>
      )}
    </StyledInputGroup>
  )
}
